import { useMutation } from "@apollo/client";
import noop from "lodash/fp/noop";
import { useAmplitude } from "../../../lib/amplitude/Amplitude";
import type { IAssetDiscussionCommentFieldsFragment } from "../../../lib/graphql/fragments/__generated__/AssetDiscussionCommentFields.generated";
import type { IAssetDiscussionCommentReplyFieldsFragment } from "../../../lib/graphql/fragments/__generated__/AssetDiscussionCommentReplyFields.generated";
import DestroyAssetDiscussionCommentReply from "../../../lib/graphql/mutations/discussions/DestroyAssetDiscussionCommentReply";
import EditAssetDiscussionCommentReply from "../../../lib/graphql/mutations/discussions/EditAssetDiscussionCommentReply";
import type {
  IDestroyAssetDiscussionCommentReplyMutation,
  IDestroyAssetDiscussionCommentReplyMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/DestroyAssetDiscussionCommentReply.generated";
import type {
  IEditAssetDiscussionCommentReplyMutation,
  IEditAssetDiscussionCommentReplyMutationVariables,
} from "../../../lib/graphql/mutations/discussions/__generated__/EditAssetDiscussionCommentReply.generated";
import transformOnDiscussionCommentsRootQuery from "../apollo/transformOnDiscussionCommentsRootQuery";
import transformOnSingleCommentRootQuery from "../apollo/transformOnSingleCommentRootQuery";

export default function useCommentReplyActions(
  reply: IAssetDiscussionCommentReplyFieldsFragment,
  rootComment: IAssetDiscussionCommentFieldsFragment,
) {
  const amplitude = useAmplitude();

  const [destroyAssetDiscussionCommentReply, { loading: isDestroyingComment, error: destroyCommentError }] =
    useMutation<IDestroyAssetDiscussionCommentReplyMutation, IDestroyAssetDiscussionCommentReplyMutationVariables>(
      DestroyAssetDiscussionCommentReply,
      {
        onError: noop,
        update: (dataProxy) => {
          transformOnDiscussionCommentsRootQuery(dataProxy, rootComment.asset._id, (rootQueryData) => {
            return {
              ...rootQueryData,
              nodes: rootQueryData.nodes.map((node) => {
                if (node._id !== rootComment._id) {
                  return node;
                }

                const nodesAfter = node.replies.nodes.filter((replyNode) => {
                  return replyNode._id !== reply._id;
                });

                return {
                  ...node,
                  replies: {
                    ...node.replies,
                    totalCount:
                      nodesAfter.length < node.replies.nodes.length
                        ? Math.max(node.replies.totalCount - 1, 0)
                        : node.replies.totalCount,
                    nodes: nodesAfter,
                  },
                };
              }),
            };
          });

          transformOnSingleCommentRootQuery(dataProxy, rootComment.shortId, (rootQueryData) => {
            if (!rootQueryData) {
              return rootQueryData;
            }

            const nodesAfter = rootQueryData.replies.nodes.filter((replyNode) => {
              return replyNode._id !== reply._id;
            });

            return {
              ...rootQueryData,
              replies: {
                ...rootQueryData.replies,
                totalCount:
                  nodesAfter.length < rootQueryData.replies.nodes.length
                    ? Math.max(rootQueryData.replies.totalCount - 1, 0)
                    : rootQueryData.replies.totalCount,
                nodes: nodesAfter,
              },
            };
          });
        },
      },
    );

  const [editAssetDiscussionCommentReply, { loading: isEditingComment, error: editCommentError }] = useMutation<
    IEditAssetDiscussionCommentReplyMutation,
    IEditAssetDiscussionCommentReplyMutationVariables
  >(EditAssetDiscussionCommentReply, {
    onError: noop,
  });

  return {
    destroyCommentError: destroyCommentError || null,
    isDestroyingComment,
    destroyComment: async () => {
      if (reply.viewerCanDestroy) {
        amplitude.logEvent("Destroyed a reply", {
          contentCharacterCount: reply.bodyAsPlainText.length,
          postedAt: reply.postedAt,
        });

        await destroyAssetDiscussionCommentReply({
          variables: {
            replyId: reply._id,
          },
        });
      }
    },

    editCommentError: editCommentError || null,
    isEditingComment,
    editComment: async (bodyAsPlainText: string) => {
      if (reply.viewerCanEdit) {
        amplitude.logEvent("Edited a reply", {
          contentCharacterCount: reply.bodyAsPlainText.length,
        });

        await editAssetDiscussionCommentReply({
          variables: {
            replyId: reply._id,
            bodyAsPlainText,
          },
        });
      }
    },
  };
}
